/* oxlint-disable confluence-feature-gating/no-preconditioning -- The master must short-circuit child gates in code to prevent child exposure in control. */
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useRelayEnvironment } from 'react-relay';

import {
	abortCommentAgentGeneration,
	reopenCommentAgentChat,
} from '@confluence/agent-mention/entry-points/commentAgentChatActions.ts';
import { getSharedCommentAgentSessions } from '@confluence/agent-mention/entry-points/sharedCommentAgentSessions.ts';
import type { ContentTypeCommentAgentReplyTarget } from '@confluence/content-types-bridge/entry-points/content-types-shell-types.ts';
import { fg } from '@confluence/feature-gating';
import {
	UNSAFE_expValEqualsNoExposure,
	isExperimentEnabled,
} from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	ContentCommentAgentSessionService,
	isCommentAgentSessionContentType,
	isCommentAgentSessionScopeValid,
	type CommentAgentSessionBridge,
} from './content-comment-agent-session-service.ts';

type SessionScope = {
	contentId: string;
	contentType: string;
	cloudId: string | null | undefined;
	userId: string | null | undefined;
	environment: ReturnType<typeof useRelayEnvironment>;
};

/**
 * Runs a Chat action for a comment's session, only if the host still holds `conversationId` for
 * `commentId` on this content and viewer. A caller's ids alone don't authorize an action.
 * Returns what the action returns, or undefined when the host refused or the action threw.
 */
function runCommentAgentChatAction<T>(
	scope: SessionScope,
	input: ContentTypeCommentAgentReplyTarget | undefined,
	action: (conversationId: string) => T,
): T | undefined {
	try {
		if (
			!isCommentAgentSessionContentType(scope.contentType) ||
			!isCommentAgentSessionScopeValid(scope.contentId, scope.cloudId, scope.userId)
		)
			return;
		// Reuse Canvas's existing cross-repo rollout gate so host and iframe disable together.
		if (
			!(
				isExperimentEnabled('canvas_agentic_experiences_master_exp') &&
				fg('canvas-rovo-comment-sessions')
			)
		)
			return;
		if (!UNSAFE_expValEqualsNoExposure('cc_ai_agent_mention_from_comment', 'isEnabled', true))
			return;
		if (
			!UNSAFE_expValEqualsNoExposure('platform_editor_inline_agentic_comments', 'isEnabled', true)
		)
			return;
		if (
			!input?.commentId ||
			!input.conversationId ||
			getSharedCommentAgentSessions(
				scope.environment,
				scope.cloudId ?? '',
				scope.userId ?? '',
				scope.contentId,
			)
				.getSnapshot()
				.conversationMap.get(input.commentId) !== input.conversationId
		)
			return;
		return action(input.conversationId);
	} catch {
		// Never throw back into the embedded content.
		return undefined;
	}
}

export function useContentCommentAgentSessionService(
	contentId: string,
	contentType = 'whiteboard',
): CommentAgentSessionBridge & {
	registry: ContentCommentAgentSessionService;
} {
	const { cloudId, userId } = useSessionData();
	const environment = useRelayEnvironment();
	const scope = useMemo(
		() => ({
			contentId,
			contentType,
			cloudId,
			userId,
			environment,
			registry: new ContentCommentAgentSessionService(`${cloudId}:${userId}:${contentId}`),
		}),
		[environment, contentId, contentType, cloudId, userId],
	);
	const latest = useRef(scope);
	useLayoutEffect(() => {
		scope.registry.replies.activate();
		latest.current = scope;
		return () => scope.registry.dispose();
	}, [scope]);

	// The bridge is constructed once; new listeners must use the committed content/viewer scope.
	const observeCommentAgentSessions = useCallback<ContentCommentAgentSessionService['observe']>(
		(onChange) => {
			const current = latest.current;
			if (
				!isCommentAgentSessionContentType(current.contentType) ||
				!isCommentAgentSessionScopeValid(current.contentId, current.cloudId, current.userId)
			) {
				onChange([]);
				return () => {};
			}
			if (
				!(
					isExperimentEnabled('canvas_agentic_experiences_master_exp') &&
					fg('canvas-rovo-comment-sessions')
				)
			) {
				onChange([]);
				return () => {};
			}
			// Registration only reads cached data. The tracking tree records actual feature exposure.
			if (!UNSAFE_expValEqualsNoExposure('cc_ai_agent_mention_from_comment', 'isEnabled', true)) {
				onChange([]);
				return () => {};
			}
			if (
				!UNSAFE_expValEqualsNoExposure('platform_editor_inline_agentic_comments', 'isEnabled', true)
			) {
				onChange([]);
				return () => {};
			}
			return current.registry.observe(onChange);
		},
		[],
	);
	const observeCommentAgentReply = useCallback<
		CommentAgentSessionBridge['observeCommentAgentReply']
	>((input, onChange) => {
		const current = latest.current;
		const unavailable = () => {
			onChange({ status: 'unavailable' });
			return () => {};
		};
		if (
			!isCommentAgentSessionContentType(current.contentType) ||
			!isCommentAgentSessionScopeValid(current.contentId, current.cloudId, current.userId)
		)
			return unavailable();
		// Reuse Canvas's existing cross-repo rollout gate so host and iframe disable together.
		if (
			!(
				isExperimentEnabled('canvas_agentic_experiences_master_exp') &&
				fg('canvas-rovo-comment-sessions')
			)
		)
			return unavailable();
		if (!UNSAFE_expValEqualsNoExposure('cc_ai_agent_mention_from_comment', 'isEnabled', true))
			return unavailable();
		if (
			!UNSAFE_expValEqualsNoExposure('platform_editor_inline_agentic_comments', 'isEnabled', true)
		)
			return unavailable();
		if (
			!input?.commentId ||
			!input.conversationId ||
			getSharedCommentAgentSessions(
				current.environment,
				current.cloudId ?? '',
				current.userId ?? '',
				current.contentId,
			)
				.getSnapshot()
				.conversationMap.get(input.commentId) !== input.conversationId
		)
			return unavailable();
		return current.registry.replies.observe(input, onChange);
	}, []);
	const resolveCommentAgentReply = useCallback<
		CommentAgentSessionBridge['resolveCommentAgentReply']
	>(async (input) => {
		const current = latest.current;
		if (
			!isCommentAgentSessionContentType(current.contentType) ||
			!isCommentAgentSessionScopeValid(current.contentId, current.cloudId, current.userId)
		)
			throw new Error('Private reply unavailable');
		// Reuse Canvas's existing cross-repo rollout gate so host and iframe disable together.
		if (
			!(
				isExperimentEnabled('canvas_agentic_experiences_master_exp') &&
				fg('canvas-rovo-comment-sessions')
			)
		)
			throw new Error('Private reply unavailable');
		if (!UNSAFE_expValEqualsNoExposure('cc_ai_agent_mention_from_comment', 'isEnabled', true))
			throw new Error('Private reply unavailable');
		if (
			!UNSAFE_expValEqualsNoExposure('platform_editor_inline_agentic_comments', 'isEnabled', true)
		)
			throw new Error('Private reply unavailable');
		// Check current host association again: a token is not authorization to another source.
		if (
			!input?.commentId ||
			!input.conversationId ||
			getSharedCommentAgentSessions(
				current.environment,
				current.cloudId ?? '',
				current.userId ?? '',
				current.contentId,
			)
				.getSnapshot()
				.conversationMap.get(input.commentId) !== input.conversationId
		)
			throw new Error('Private reply changed');
		return current.registry.replies.resolve(input);
	}, []);
	const stopCommentAgentSession = useCallback<CommentAgentSessionBridge['stopCommentAgentSession']>(
		async (input) =>
			(await runCommentAgentChatAction(latest.current, input, abortCommentAgentGeneration)) ===
			true,
		[],
	);
	const openCommentAgentChat = useCallback<CommentAgentSessionBridge['openCommentAgentChat']>(
		(input) => runCommentAgentChatAction(latest.current, input, reopenCommentAgentChat),
		[],
	);
	return {
		registry: scope.registry,
		observeCommentAgentSessions,
		observeCommentAgentReply,
		resolveCommentAgentReply,
		stopCommentAgentSession,
		openCommentAgentChat,
	};
}
